import { escapeHTML, sanitizeHelper } from './security';
import { FormattingHelper } from './isformatted';
import { BlockFactory } from '../../block-manager/services/block-factory';
import { BlockType, ContentType } from '../../models/enums';
import { buildTableClipboardPayload, extractPlainTextMatrixFromPayload, htmlTableFromMatrix } from './clipboard-utils';
import { focusAllCellsInTable, removeFocusFromAllCells } from './table-utils';
const hasBlockElements = (element) => {
    const blockTags = ['H1', 'H2', 'H3', 'H4', 'BLOCKQUOTE', 'UL', 'OL', 'PRE', 'HR', 'IMG', 'TABLE'];
    return blockTags.some((tag) => element.querySelector(tag) !== null);
};
export function convertHtmlElementToBlocks(container, keepFormat) {
    const blocks = [];
    const processNode = (node, indentLevel = 0) => {
        let isBlockProcessed = false;
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() === '') {
            return isBlockProcessed;
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node;
            const tag = element.tagName;
            if (tag === 'UL' || tag === 'OL') {
                const isOrdered = tag === 'OL';
                const children = Array.from(element.children);
                for (const child of children) {
                    if (child.tagName === 'LI') {
                        // Create list item block from <li> content
                        blocks.push(BlockFactory.createBlockFromPartial({
                            blockType: isOrdered ? BlockType.NumberedList : BlockType.BulletList,
                            content: convertInlineElementsToContentModels(child, keepFormat),
                            indent: indentLevel
                        }));
                        // Recurse into **nested lists** inside this <li>
                        Array.from(child.children).forEach((nested) => {
                            if (nested.tagName === 'UL' || nested.tagName === 'OL') {
                                processNode(nested, indentLevel + 1);
                            }
                        });
                    }
                    if (child.tagName === 'UL' || child.tagName === 'OL') {
                        // Recurse into **nested lists** if any, inside this <ul>
                        processNode(child, indentLevel + 1);
                    }
                    isBlockProcessed = true;
                }
            }
            else if (tag.match(/^H[1-4]$/)) {
                const level = parseInt(tag.substring(1), 10);
                blocks.push(BlockFactory.createHeadingBlock({ content: convertInlineElementsToContentModels(element, keepFormat) }, { level: level }));
                isBlockProcessed = true;
            }
            else if (tag === 'BLOCKQUOTE') {
                blocks.push(BlockFactory.createQuoteBlock({ content: convertInlineElementsToContentModels(element, keepFormat) }));
                isBlockProcessed = true;
            }
            else if (tag === 'HR') {
                blocks.push(BlockFactory.createDividerBlock());
                isBlockProcessed = true;
            }
            else if (tag === 'IMG') {
                const sanitizedHtml = sanitizeHelper(element.outerHTML, true);
                const temp = document.createElement('div');
                temp.innerHTML = sanitizedHtml;
                const img = temp.firstChild;
                if (img) {
                    blocks.push(BlockFactory.createImageBlock({}, {
                        src: img.src,
                        altText: img.alt
                    }));
                }
                isBlockProcessed = true;
            }
            else if (tag === 'PRE' && element.querySelector('code')) {
                blocks.push(BlockFactory.createCodeBlock({ content: [BlockFactory.createTextContent({ content: element.textContent })] }));
                isBlockProcessed = true;
            }
            else if ((tag === 'P') && !hasBlockElements(element)) {
                blocks.push(BlockFactory.createParagraphBlock({ content: convertInlineElementsToContentModels(element, keepFormat) }));
                isBlockProcessed = true;
            }
            else if (tag === 'TABLE') {
                const model = parseHtmlTableToBlock(element);
                if (model) {
                    blocks.push(model);
                }
                isBlockProcessed = true;
            }
            if ((tag === 'DIV') && !hasBlockElements(element)) {
                blocks.push(BlockFactory.createParagraphBlock({ content: convertInlineElementsToContentModels(element, keepFormat) }));
                isBlockProcessed = true;
            }
        }
        else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '') {
            blocks.push(BlockFactory.createParagraphBlock({
                content: [BlockFactory.createTextContent({ content: node.textContent.trim() })]
            }));
            isBlockProcessed = true;
        }
        return isBlockProcessed;
    };
    const traverseDOM = (element) => {
        const isBlockProcessed = processNode(element);
        if (!isBlockProcessed && element.nodeType === Node.ELEMENT_NODE) {
            const tagName = element.tagName;
            // Skip traversing children for elements that are processed as whole units
            const ignorableNestedChildren = ['PRE', 'HR', 'IMG', 'TABLE'].indexOf(tagName) !== -1;
            if (!ignorableNestedChildren) {
                Array.from(element.childNodes).forEach(traverseDOM);
            }
        }
    };
    Array.from(container.childNodes).forEach((node) => {
        traverseDOM(node);
    });
    return blocks;
}
export function convertInlineElementsToContentModels(element, keepFormat) {
    const content = [];
    if (!keepFormat) {
        if (element.textContent !== '') {
            return [BlockFactory.createTextContent({ content: element.textContent })];
        }
    }
    const styleStack = [{}];
    let linkProps;
    const processNode = (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            let text = node.textContent;
            text = text.replace(/\n/g, '');
            if (text !== '') {
                content.push(createContentModel(text, styleStack[styleStack.length - 1], linkProps));
            }
            return;
        }
        const el = node;
        if (el.classList.contains('e-mention-chip')) {
            let chipModel;
            if (el.classList.contains('e-user-chip')) {
                const userId = el.getAttribute('data-user-id');
                chipModel = BlockFactory.createMentionContent({ content: el.textContent }, { userId });
            }
            else {
                const labelId = el.getAttribute('data-label-id');
                chipModel = BlockFactory.createLabelContent({ content: el.textContent }, { labelId });
            }
            content.push(chipModel);
            return;
        }
        if (el.tagName === 'UL' || el.tagName === 'OL') {
            return;
        }
        if (el.tagName === 'A') {
            const prevLink = linkProps;
            linkProps = {
                url: el.getAttribute('href') || ''
            };
            processChildren(el);
            linkProps = prevLink;
            return;
        }
        // <br> → insert linebreak
        if (el.tagName === 'BR') {
            content.push(createContentModel('\n', styleStack[styleStack.length - 1], linkProps));
            return;
        }
        const newStyles = extractStylesFromElement(el, styleStack[styleStack.length - 1]);
        if (newStyles && Object.keys(newStyles).length > 0) {
            styleStack.push(newStyles);
            processChildren(el);
            styleStack.pop();
        }
        else {
            processChildren(el);
        }
    };
    const processChildren = (parent) => {
        Array.from(parent.childNodes).forEach(processNode);
    };
    const createContentModel = (text, styles, link) => {
        if (link) {
            return BlockFactory.createLinkContent({
                content: text
            }, {
                styles: Object.assign({}, styles),
                url: link.url
            });
        }
        return BlockFactory.createTextContent({ content: text }, { styles: Object.assign({}, styles) });
    };
    processChildren(element);
    return content;
}
export function extractStylesFromElement(element, styles = {}) {
    const newStyles = Object.assign({}, styles);
    const isBold = FormattingHelper.isBold(element);
    if (isBold) {
        newStyles.bold = isBold;
    }
    const isItalic = FormattingHelper.isItalic(element);
    if (isItalic) {
        newStyles.italic = isItalic;
    }
    const isUnderline = FormattingHelper.isUnderline(element);
    if (isUnderline) {
        newStyles.underline = isUnderline;
    }
    const isStrikethrough = FormattingHelper.isStrikethrough(element);
    if (isStrikethrough) {
        newStyles.strikethrough = isStrikethrough;
    }
    const isSuperscript = FormattingHelper.isSuperscript(element);
    if (isSuperscript) {
        newStyles.superscript = isSuperscript;
    }
    const isSubscript = FormattingHelper.isSubscript(element);
    if (isSubscript) {
        newStyles.subscript = isSubscript;
    }
    if (element.style.color) {
        newStyles.color = FormattingHelper.getFontColor(element);
    }
    if (element.style.backgroundColor) {
        newStyles.backgroundColor = FormattingHelper.getBackgroundColor(element);
    }
    const isInlineCode = FormattingHelper.isInlineCode(element);
    if (isInlineCode) {
        newStyles.inlineCode = isInlineCode;
    }
    const isUppercase = element.style.textTransform === 'uppercase';
    if (isUppercase) {
        newStyles.uppercase = isUppercase;
    }
    const isLowercase = element.style.textTransform === 'lowercase';
    if (isLowercase) {
        newStyles.lowercase = isLowercase;
    }
    const isLink = FormattingHelper.isLink(element);
    if (isLink) {
        newStyles.url = FormattingHelper.getLinkUrl(element);
    }
    return newStyles;
}
/**
 * Detects all active formats for a specific text node.
 * Traverses upward through DOM to find format-containing ancestors.
 *
 * @param {Text} node - The text node to analyze
 * @returns {Styles} - All detected formats on this node
 * @hidden
 */
export function detectFormatsForTextNode(node) {
    const merged = {};
    let currentElement = node.parentElement;
    while (currentElement) {
        // Stop at block boundary (element with id)
        if (currentElement.id) {
            break;
        }
        const newStyles = extractStylesFromElement(currentElement, merged);
        Object.assign(merged, newStyles);
        currentElement = currentElement.parentElement;
    }
    return merged;
}
export function parseHtmlTableToMatrix(root) {
    const table = (root.tagName === 'TABLE' ? root : root.querySelector('table'));
    if (!table) {
        return { matrix: [], hasHeader: false };
    }
    const thead = table.querySelector('thead');
    const tbody = table.querySelector('tbody');
    const hasHeader = !!thead && !!thead.rows && thead.rows.length > 0;
    const rows = [];
    const matrix = [];
    if (thead && thead.rows && thead.rows.length) {
        rows.push(thead.rows[0]);
    }
    if (tbody) {
        Array.from(tbody.querySelectorAll('tr')).forEach((tr) => rows.push(tr));
    }
    for (const tr of rows) {
        const cells = Array.from(tr.querySelectorAll('th,td'));
        // Ignore row-number column if present by class
        const filtered = cells.filter((c) => !c.classList.contains('e-row-number'));
        matrix.push(filtered);
    }
    // Normalize to rectangular grid by padding shorter rows
    const maxCols = Math.max(0, ...matrix.map((r) => r.length));
    matrix.forEach((row) => {
        while (row.length < maxCols) {
            row.push(document.createElement('td'));
        }
    });
    return { matrix, hasHeader };
}
export function parseHtmlTableToBlock(element) {
    const { matrix, hasHeader } = parseHtmlTableToMatrix(element);
    if (!matrix.length) {
        return null;
    }
    const bodyStart = hasHeader ? 1 : 0;
    const colsCount = matrix[0].length;
    // Build columns with header text (plain for header)
    const columns = new Array(colsCount).fill(0).map((_, idx) => ({
        id: `col_${idx}`, width: '', headerText: hasHeader ? (matrix[0][idx].textContent).trim() : ''
    }));
    // Build row models with blocks per cell using existing conversion
    const rows = [];
    for (let r = bodyStart; r < matrix.length; r++) {
        const cells = [];
        for (let c = 0; c < colsCount; c++) {
            const cellEl = matrix[r][c];
            const blocks = hasBlockElements(cellEl) ? convertHtmlElementToBlocks(cellEl, true) : [];
            // Fallback to plain text paragraph if no blocks detected
            const cellBlocks = blocks.length
                ? blocks
                : [BlockFactory.createParagraphBlock({ content: convertInlineElementsToContentModels(cellEl, true) })];
            cells.push({ columnId: columns[c].id, blocks: cellBlocks });
        }
        rows.push({ cells });
    }
    const properties = {
        enableHeader: hasHeader,
        enableRowNumbers: true,
        columns,
        rows
    };
    return BlockFactory.createBlockFromPartial({ blockType: 'Table', properties });
}
export function getBlockDataAsHTML(blocks, editorId) {
    if (!blocks || blocks.length === 0) {
        return '';
    }
    const htmlParts = [];
    const listStack = [];
    let currentIndent = -1;
    const closeListsToLevel = (targetIndent) => {
        while (listStack.length > 0 && listStack[parseInt((listStack.length - 1).toString(), 10)].indent >= targetIndent) {
            const finishedList = listStack.pop();
            const listHtml = `<${finishedList.type}>${finishedList.items.map((item) => item + '</li>').join('')}</${finishedList.type}>`;
            if (listStack.length > 0) {
                const parentList = listStack[parseInt((listStack.length - 1).toString(), 10)];
                const lastIdx = parentList.items.length - 1;
                parentList.items[parseInt(lastIdx.toString(), 10)] += listHtml;
            }
            else {
                htmlParts.push(listHtml);
            }
        }
    };
    for (const block of blocks) {
        if (!block) {
            continue;
        }
        const isList = ['bulletlist', 'numberedlist', 'checklist'].indexOf(block.blockType.toLowerCase()) !== -1;
        const listType = block.blockType.toLowerCase() === 'numberedlist' ? 'ol' : 'ul';
        const indent = block.indent || 0;
        if (isList) {
            // Close deeper lists when moving to a higher level (less indentation)
            if (listStack.length === 0 ||
                listStack[listStack.length - 1].type !== listType ||
                indent < currentIndent) {
                closeListsToLevel(indent);
            }
            // Start new list if:
            // 1. First list item
            // 2. Different list type
            // 3. Increased indentation
            if (listStack.length === 0 ||
                listStack[parseInt((listStack.length - 1).toString(), 10)].type !== listType ||
                indent > currentIndent) {
                listStack.push({
                    type: listType,
                    items: [],
                    indent
                });
                currentIndent = indent;
            }
            const contentHtml = renderContentAsHTML(block.content);
            listStack[parseInt((listStack.length - 1).toString(), 10)].items.push(`<li>${contentHtml}`);
        }
        else {
            closeListsToLevel(0);
            currentIndent = -1;
            htmlParts.push(renderBlockAsHTML(block, editorId));
        }
    }
    closeListsToLevel(0);
    return htmlParts.join('');
}
export function renderContentAsHTML(content = []) {
    return content
        .map((item) => {
        if (!item || !item.content) {
            return '';
        }
        let text = escapeHTML(item.content);
        const props = item.properties;
        const styles = (props && props.styles) ? props.styles : {};
        const keys = Object.keys(styles);
        if (keys.length > 0) {
            for (const style of keys) {
                if (styles[style]) {
                    switch (style.toLowerCase()) {
                        case 'bold':
                            text = `<strong>${text}</strong>`;
                            break;
                        case 'italic':
                            text = `<em>${text}</em>`;
                            break;
                        case 'underline':
                            text = `<u>${text}</u>`;
                            break;
                        case 'strikethrough':
                            text = `<s>${text}</s>`;
                            break;
                        case 'superscript':
                            text = `<sup>${text}</sup>`;
                            break;
                        case 'subscript':
                            text = `<sub>${text}</sub>`;
                            break;
                        case 'uppercase':
                            text = `<span style="text-transform: uppercase;">${text}</span>`;
                            break;
                        case 'lowercase':
                            text = `<span style="text-transform: lowercase;">${text}</span>`;
                            break;
                        case 'color':
                            text = `<span style="color: ${styles.color};">${text}</span>`;
                            break;
                        case 'backgroundcolor':
                            text = `<span style="background-color: ${styles.backgroundColor};">${text}</span>`;
                            break;
                        case 'inlinecode':
                            text = `<code>${text}</code>`;
                            break;
                    }
                }
            }
        }
        if (item.contentType === ContentType.Link && item.properties) {
            const props = item.properties;
            const target = 'target="_blank"';
            text = `<a href="${escapeHTML(props.url)}" ${target}>${text}</a>`;
        }
        return text;
    }).join('');
}
export function renderBlockAsHTML(block, editorId) {
    const contentHTML = renderContentAsHTML(block.content);
    switch (block.blockType.toLowerCase()) {
        case 'heading': {
            const level = block.properties.level;
            return renderElementWithWrapper(`h${level}`, contentHTML);
        }
        case 'paragraph':
            return renderElementWithWrapper('p', contentHTML);
        case 'quote': {
            const children = block.properties.children;
            const childrenHTML = children && children.map((child) => renderBlockAsHTML(child)).join('') || '';
            return renderElementWithWrapper('blockquote', childrenHTML);
        }
        case 'callout': {
            const children = block.properties.children;
            const childrenHTML = children && children.map((child) => renderBlockAsHTML(child)).join('') || '';
            return renderElementWithWrapper('div', childrenHTML, 'callout');
        }
        case 'divider':
            return '<hr />';
        case 'code':
            return `<pre><code>${block.content[0].content}</code></pre>`;
        case 'image': {
            const props = block.properties;
            if (props && props.src === '') {
                return '';
            }
            return `<img src='${props.src}' alt='${props.altText}' />`;
        }
        case 'collapsibleparagraph':
        case 'collapsibleheading': {
            const children = block.properties.children;
            const childrenHTML = children && children.map((child) => renderBlockAsHTML(child)).join('') || '';
            return renderElementWithWrapper('div', (contentHTML + ' ' + childrenHTML), 'collapsible');
        }
        case 'table': {
            const editorRoot = document.querySelector(`#${editorId}`);
            const tableBlockEl = editorRoot.querySelector(`#${block.id}`);
            focusAllCellsInTable(tableBlockEl);
            const payload = buildTableClipboardPayload(tableBlockEl, block);
            const matrixText = extractPlainTextMatrixFromPayload(payload, block);
            const html = htmlTableFromMatrix(matrixText, { hasHeader: payload.meta.enableHeader, hasRowNumbers: payload.meta.enableRowNumbers });
            removeFocusFromAllCells(tableBlockEl);
            return html;
        }
        default:
            return renderElementWithWrapper('div', contentHTML);
    }
}
export function renderElementWithWrapper(tagName, content, className) {
    const classAttr = className ? ` class="${className}"` : '';
    return `<${tagName}${classAttr}>${content}</${tagName}>`;
}
